import React, { useMemo } from "react";
import { Box, Avatar, Typography, Paper, useTheme, Stack } from "@mui/material";
import {
  useTranslationContext,
  MessageDeliveryStatus,
} from "stream-chat-react";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { DoneAll } from "@mui/icons-material";

const CustomChannelPreview = ({
  channel,
  activeChannel,
  displayImage,
  displayTitle,
  latestMessagePreview,
  setActiveChannel,
  messageDeliveryStatus,
}) => {
  const theme = useTheme();
  const { userLanguage } = useTranslationContext();
  const latestMessageAt = channel.state.last_message_at;
  const isSelected = channel.id === activeChannel?.id;

  const timestamp = useMemo(() => {
    if (!latestMessageAt) return "";
    const formatter = new Intl.DateTimeFormat(userLanguage, {
      timeStyle: "short",
    });
    return formatter.format(latestMessageAt);
  }, [latestMessageAt, userLanguage]);

  const handleClick = () => {
    setActiveChannel?.(channel);
  };

  return (
    <Paper
      component="button"
      onClick={handleClick}
      disabled={isSelected}
      elevation={0}
      sx={{
        all: "unset",
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "90%",
        mx: 0.5,
        my: 0.5,
        p: 1.2,
        borderRadius: 2,
        border: "2px solid transparent",
        cursor: isSelected ? "default" : "pointer",
        bgcolor: isSelected
          ? theme.palette.background.paper
          : theme.palette.background.default,
        borderColor: isSelected ? theme.palette.primary.main : "transparent",
        "&:hover": {
          backgroundColor: !isSelected
            ? theme.palette.action.hover
            : theme.palette.background.paper,
        },
      }}
    >
      {/* Avatar */}
      <Avatar
        src={displayImage}
        alt={displayTitle}
        sx={{
          width: 56,
          height: 56,
          objectFit: "cover",
          borderRadius: "50%",
        }}
      />

      {/* Text Area */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Header: Name + Time */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 0.5, mx: 0 }}
        >
          <Typography
            variant="subtitle1"
            noWrap
            sx={{
              fontWeight: 600,
              color: isSelected
                ? theme.palette.primary.main
                : theme.palette.text.primary,
            }}
          >
            {displayTitle}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 400 }}
          >
            {timestamp}
          </Typography>
        </Stack>
        {/* Last Message + Status */}
        <Stack direction="row" alignItems="center" 
          justifyContent="space-between" spacing={0.5} mb={0.5}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: "text.secondary",
              p: 0,
            }}
          >
            {latestMessagePreview.props?.children || "No messages yet"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
            {messageDeliveryStatus === MessageDeliveryStatus.DELIVERED && (
              <DoneAllIcon
                style={{ width: 16, height: 16, color: "#9e9e9e" }}
              />
            )}
            {messageDeliveryStatus === MessageDeliveryStatus.READ && (
              <DoneAllIcon
                style={{ width: 16, height: 16, color: "#4caf50" }}
              />
            )}
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default CustomChannelPreview;
